/**
 * multiYear — the 3-year rollup for the EBITDA Normalization workspace.
 *
 * WS-VIEW-2. The workspace could only ever show one fiscal year at a time, so
 * the trend — the thing a buyer actually underwrites — was invisible until the
 * report was generated.
 *
 * SCOPE — this is multi-YEAR: one entity, several periods. Combining separate
 * legal entities is multi-ENTITY consolidation, a different feature with its
 * own eliminations, NCI and ownership maths (CONSOL-FIX-1 / ENG-8, see
 * `listDealEntities` / `runConsolidation` in @/api/client). Nothing here
 * consolidates anything.
 *
 * NO NEW MATH. Every per-year figure is `fm.derivePeriod()` — the same
 * recompute-on-read the report engine uses (TS mirror of
 * core/financial_math.derive_period). This module only *selects* and
 * *aggregates* those outputs; it never re-derives a P&L figure itself, so a
 * formula change in financialMath propagates here for free and the trend can
 * never disagree with the Summary panel.
 *
 * The two aggregates are deliberately the least surprising ones available:
 *   TTM      = the most recent populated year, carried through unchanged. This
 *              matches the report side, where `periods_to_qoe_context` sets
 *              `ctx.ttm` from `periods[-1]` (core/ingestion/pl_parser.py) —
 *              with annual data and no interim stub, trailing twelve months
 *              *is* the latest full year. No annualisation, no interpolation.
 *   3-yr avg = the arithmetic mean of the populated years, which is why
 *              `years_used` is reported alongside it: an average over 2 of 3
 *              years must never be read as an average over 3.
 * Both reconcile to the per-year rows to the dollar, by construction.
 */

import * as fm from './financialMath'
import type { AdjustmentLike, Cell } from './financialMath'
import { routeFlexibleItems, type FlexibleLineItem } from './flexibleLineItems'

/** One P&L year exactly as the workspace holds it. */
export interface YearRaw extends fm.RawPeriod {}

/** An adjustment row, which additionally knows the year it belongs to. */
export interface YearAdjustment extends AdjustmentLike {
  year?: number | null
}

/** One row of the trend: the engine's own derived figures for that year. */
export interface YearRollup {
  year: number
  revenue: Cell
  gross_profit: Cell
  ebitda: Cell
  adjusted_ebitda: Cell
  sde: Cell
  owner_comp_total: number
  /**
   * True when the analyst has entered something for this year. A year with no
   * revenue and no EBITDA is *absent*, not zero — plotting it as a 0 bar
   * invents a collapse that did not happen.
   */
  populated: boolean
}

export interface MultiYearSummary {
  rows: YearRollup[]
  /** Populated rows only, oldest first. The trend and both aggregates use these. */
  populatedRows: YearRollup[]
  /** Most recent populated year, or null when nothing has been entered yet. */
  ttm: YearRollup | null
  /** Simple mean of `populatedRows`. null when nothing is populated. */
  average: {
    years_used: number
    revenue: number | null
    gross_profit: number | null
    ebitda: number | null
    adjusted_ebitda: number | null
    sde: number | null
  } | null
}

const filled = (v: Cell | null | undefined): v is number =>
  typeof v === 'number' && Number.isFinite(v)

/** Mean of the filled values only; null when none are filled. */
function meanOf(rows: YearRollup[], pick: (r: YearRollup) => Cell): number | null {
  const values = rows.map(pick).filter(filled)
  if (values.length === 0) return null
  return values.reduce((a, b) => a + b, 0) / values.length
}

/**
 * Derive one year. `adjustments` and `flexibleItems` must each be the full,
 * unfiltered deal-level list — filtering to the year happens here (and inside
 * `derivePeriod` for the flexible items) so the caller cannot get it wrong.
 *
 * FIELDS-2: `year` is injected into the raw period on the way into
 * `derivePeriod`, because that is how a flexible item finds its period. The
 * workspace's P&L map is keyed BY year and so its rows carry no `year` field of
 * their own; without this the trend would silently route nothing and every row
 * would disagree with the Summary panel for the same fiscal year.
 */
export function rollupYear(
  year: number,
  raw: YearRaw | undefined | null,
  adjustments: YearAdjustment[] | null | undefined,
  flexibleItems?: FlexibleLineItem[] | null,
): YearRollup {
  const period = raw ?? {}
  const yearAdjustments = (adjustments ?? []).filter(a => a.year === year)
  const d = fm.derivePeriod({ ...period, year }, yearAdjustments, flexibleItems)
  // Reported revenue plus this year's `revenue`-treatment items, matching what
  // `derivePeriod` computed gross profit from. `populated` deliberately does NOT
  // consider flexible items: derivePeriod's blank gates mean a year with items
  // but no reported revenue or COGS still derives to nothing, so counting it as
  // populated would plot an all-empty row and drag the 3-year average.
  const routed = routeFlexibleItems(flexibleItems, year)
  const revenue = filled(period.revenue)
    ? period.revenue + routed.revenueDelta
    : period.revenue ?? ''
  return {
    year,
    revenue,
    gross_profit: d.gross_profit,
    ebitda: d.ebitda,
    adjusted_ebitda: d.adjusted_ebitda,
    sde: d.sde,
    owner_comp_total: d.owner_comp_total,
    // An adjustment on its own counts: a year can legitimately hold nothing but
    // add-backs while the P&L import is still pending.
    populated:
      filled(period.revenue) ||
      filled(period.cogs) ||
      filled(period.operating_expenses) ||
      yearAdjustments.length > 0,
  }
}

/**
 * The whole rollup for a deal. `years` is ordered oldest-first by the caller
 * (the workspace passes [CY-2, CY-1, CY]); TTM is taken from the last
 * populated entry in that order, so ordering is the caller's contract.
 */
export function buildMultiYear(
  years: readonly number[],
  pl: Record<number, YearRaw> | null | undefined,
  adjustments: YearAdjustment[] | null | undefined,
  flexibleItems?: FlexibleLineItem[] | null,
): MultiYearSummary {
  const rows = years.map(y => rollupYear(y, pl?.[y], adjustments, flexibleItems))
  const populatedRows = rows.filter(r => r.populated)
  const ttm = populatedRows.length ? populatedRows[populatedRows.length - 1] : null

  const average = populatedRows.length
    ? {
        years_used: populatedRows.length,
        revenue: meanOf(populatedRows, r => r.revenue),
        gross_profit: meanOf(populatedRows, r => r.gross_profit),
        ebitda: meanOf(populatedRows, r => r.ebitda),
        adjusted_ebitda: meanOf(populatedRows, r => r.adjusted_ebitda),
        sde: meanOf(populatedRows, r => r.sde),
      }
    : null

  return { rows, populatedRows, ttm, average }
}

/**
 * Year-over-year growth on a metric, as a fraction (0.12 = +12%). null when
 * either year is unfilled or the base is zero — a growth rate off a zero base
 * is undefined, not infinite.
 */
export function yoyGrowth(current: Cell, prior: Cell): number | null {
  if (!filled(current) || !filled(prior) || prior === 0) return null
  return (current - prior) / Math.abs(prior)
}
